import { clsx } from 'clsx';
import { Credit } from '@types';
import { Button, Modal } from 'antd';
import React, { useState } from 'react';
import { useNotify } from 'react-admin';
import { InitialCard, InitialRow, SwitchInput } from '@components';

import { rbac } from '@utils';
import { creditHttp } from '@network';
import { useModalState } from '@hooks';
import { ExportOrdersButton } from '@pages/credit/ExportOrdersButton';
import { CreditInterestWidget } from '@pages/credit/CreditEdit/CreditAmounts/CreditInterestWidget';
import cls from './CreditActions.module.css';

interface Props {
  credit: Credit;
  editable: boolean;
}

export const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20" fill="none">
    <g clipPath="url(#clip0_4609_21744)">
      <path d="M13.9203 3.25775H17.3962C17.3526 3.18441 17.2946 3.11816 17.2256 3.06191L13.6639 0.191081C13.5676 0.112747 13.4512 0.0556641 13.3242 0.0244141L13.3856 2.79775C13.3908 3.05191 13.628 3.25608 13.9203 3.25775Z" style={{ fill: 'var(--primary-color)' }}/>
      <path d="M13.9198 3.9475C13.1972 3.94375 12.6093 3.44083 12.5925 2.81292L12.5302 0H1.78394C0.797334 0.0025 -0.000957471 0.699167 8.61942e-07 1.55708V18.4429C-0.000957471 19.3008 0.797334 19.9975 1.78394 20H15.7056C16.6923 19.9975 17.4905 19.3008 17.4896 18.4429V3.9475H13.9198Z" style={{ fill: 'var(--primary-color)' }}/>
      <path d="M11.9439 12.1964L9.84462 14.0164C9.60791 14.2214 9.29406 14.3443 8.96056 14.3627C8.93133 14.366 8.9021 14.3677 8.87287 14.3681C8.84748 14.3693 8.82208 14.3693 8.79669 14.3681C8.46271 14.3552 8.14598 14.2368 7.90496 14.0356L5.77075 12.3764C5.47127 12.1448 5.44444 11.746 5.71085 11.4856C5.97727 11.2252 6.43583 11.2018 6.73531 11.4335L8.15508 12.5314V7.29393C8.14118 6.9456 8.45456 6.6531 8.85562 6.64101C9.25621 6.62893 9.59258 6.90143 9.60648 7.25018C9.60696 7.26476 9.60696 7.27935 9.60648 7.29393V12.4443L10.9218 11.3006C11.2064 11.0552 11.6659 11.0564 11.9482 11.3039C12.2304 11.5514 12.2285 11.951 11.9443 12.1964H11.9439Z" fill="#E1E5ED"/>
    </g>
    <defs>
      <clipPath id="clip0_4609_21744">
        <rect width="17.4896" height="20" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

export const CreditActions = ({ credit, editable }: Props) => {
  const notify = useNotify();
  const [isLoading, setIsLoading] = useState(false);
  const [parDelayDays, setParDelayDays] = useState(credit.parDelayDays || '');
  const { is: isModalOpened, hide: hideModal, show: showModal } = useModalState();

  const onParDelayDaysChange = (e: any) => {
    if (!editable) return;
    const value = e.target.value.replace(/\D/g, '');
    setParDelayDays(value);
  };

  const recalculate = async () => {
    if (!editable) return;
    setIsLoading(true);
    try {
      await creditHttp.updateParDelayDays(credit.id, +parDelayDays);
      notify('Updated successfully');
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <InitialCard
      left="Other Actions"
      autoHeight
      extraPadding
      minHeight={250}
    >
      <div className={cls.wrap}>
        <div className={cls.actions}>
          {rbac.isSuperadmin() && (
            <InitialRow
              title="Stop Tasks generation:"
              labelSize={18}
              className={cls.actionTitle}
            >
              <SwitchInput label={false} source="noCalls" disabled={!editable} />
            </InitialRow>
          )}
          <InitialRow
            title="Is Fixed:"
            tip="Is Fixed for updates of Risk Manager answers, Questionario"
            labelSize={18}
            className={cls.actionTitle}
          >
            <SwitchInput label={false} source="isFixed" disabled />
          </InitialRow>

          <div className={cls.recalculate}>
            <input
              type="text"
              className={cls.recalculateInput}
              placeholder="DoD"
              disabled={!editable}
              value={parDelayDays}
              onChange={onParDelayDaysChange} />
            <span>Recalculate</span>
            <button
              className={clsx(
                cls.recalculateBtn,
                isLoading && cls.loading,
                !editable && cls.disabled,
              )}
              type="button"
              onClick={recalculate}
              disabled={!editable || isLoading}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none">
                <path d="M18.6678 7.39261C18.2355 5.33525 17.1388 3.48704 15.5548 2.14622C13.9707 0.8054 11.9917 0.0501748 9.93705 0.00241295C7.88239 -0.0453489 5.87194 0.617139 4.23011 1.88297C2.58829 3.14881 1.41085 4.94415 0.887809 6.97931L2.60736 7.44268C3.02868 5.80333 3.97713 4.35714 5.29965 3.33749C6.62217 2.31783 8.24163 1.78419 9.89669 1.82266C11.5518 1.86113 13.1459 2.46948 14.4219 3.54954C15.6978 4.6296 16.5812 6.11836 16.9294 7.7756L18.6678 7.39261Z" fill="white"/>
                <path d="M18.5432 11.2505L20 6.62028L15.3556 7.64356L18.5432 11.2505Z" fill="white"/>
                <path d="M1.26997 11.3088C1.81715 13.1913 2.9287 14.8499 4.44727 16.0498C5.96584 17.2497 7.81451 17.9302 9.73161 17.9949C11.6487 18.0596 13.5371 17.5053 15.1296 16.4104C16.722 15.3155 17.9378 13.7354 18.6049 11.8939L16.9378 11.2607C16.4005 12.7441 15.4211 14.0168 14.1384 14.8988C12.8556 15.7808 11.3345 16.2273 9.79019 16.1752C8.24593 16.1231 6.75679 15.5749 5.53356 14.6084C4.31032 13.6418 3.41495 12.3058 2.97418 10.7894L1.26997 11.3088Z" fill="white"/>
                <path d="M1.10299 7.45253L0 12.1842L4.55332 10.7965L1.10299 7.45253Z" fill="white"/>
              </svg>
            </button>
          </div>
        </div>
        <div className={cls.buttons}>
          <Button className={cls.download} onClick={showModal}>
            <DownloadIcon />
            <span>Interest Table</span>
          </Button>
          <ExportOrdersButton credit={credit} buttonClass={cls.download} />
        </div>
      </div>

      <Modal
        title={null}
        open={isModalOpened}
        onCancel={hideModal}
        closeIcon={null}
        footer={null}
        wrapClassName={cls.modalWrap}
        width={1180}
      >
        <CreditInterestWidget
          creditId={credit.id}
          credit={credit}
          isModalVisible={false}
          isPreview={true}
        />
      </Modal>
    </InitialCard>
  );
};
